import { Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import Button from '../components/form/components/Button.component';
import { Form } from '../components/form/Form';
import { useFilter } from '../core/filter';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { getItem } from '../utility/Storage.utility';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const SubmitButton = () => {
  return (
    <Button type='submit' variant='contained' startIcon={<FilterListIcon />}>
      Search
    </Button>
  );
};

const ResetButton = ({ handleResetFilter, initialState }) => {
  const { reset } = useFormContext();

  const onReset = useCallback(() => {
    handleResetFilter();
    reset(initialState);
  }, [handleResetFilter, reset, initialState]);

  return (
    <Button
      variant='outlined'
      color='error'
      startIcon={<ClearIcon />}
      onClick={onReset}
    >
      Reset
    </Button>
  );
};

const FilterView = ({ filter, tag, children, list }) => {
  const dispatch = useDispatch();

  const { value } = useSelector((state) => state[tag].filter);
  const {
    list: {
      selected,
      pagination: { pageSize },
    },
  } = useSelector((state) => state[tag]);
  const {
    conf: { schema, initialState, fields },
    actions: { filter: filterAction },
  } = filter;

  const { handleChangeFilter } = useFilter(filterAction);
  const { from, to } = useSelector((state) => state.year);
  const { pathname } = useLocation();

  const defaultValues = useMemo(() => {
    return value || initialState.value;
  }, [value, initialState]);

  const onSubmit = useCallback(
    (values) => {
      if (!_.isEmpty(selected)) dispatch(list.actions.resetSelect());
      dispatch(list?.actions?.pagination({ page: 1, pageSize: pageSize }));
      handleChangeFilter({ ...values, season: from, season_to: to });
    },
    [handleChangeFilter, from, to, dispatch, list, selected, pageSize]
  );

  const updateDatesFilter = useCallback(
    ({ newInitialState, syncWithSeason = false }) => {
      const updatedState = { ...newInitialState };

      if (syncWithSeason) {
        if (Object.prototype.hasOwnProperty.call(updatedState, 'start_date')) {
          updatedState.start_date = dayjs.utc(from).format('YYYY-MM-DD');
        }
        if (Object.prototype.hasOwnProperty.call(updatedState, 'end_date')) {
          updatedState.end_date = dayjs.utc(to).format('YYYY-MM-DD');
        }
      }

      handleChangeFilter(updatedState);
    },
    [from, to, handleChangeFilter]
  );

  const onReset = useCallback(() => {
    if (!_.isEmpty(selected)) dispatch(list.actions.resetSelect());

    updateDatesFilter({
      newInitialState: { ...initialState.value, season: from, season_to: to },
      syncWithSeason: true,
    });
  }, [initialState, updateDatesFilter, from, to, dispatch, list, selected]);

  useEffect(() => {
    const filtersStorage = getItem(tag)?.filters;
    const isSeasonChanged =
      filtersStorage?.season !== from || filtersStorage?.season_to !== to;
    const thisYear = dayjs.utc().year();
    const isCurrentSeason =
      from &&
      to &&
      dayjs.utc(from).year() === thisYear &&
      dayjs.utc(to).year() === thisYear;

    if (isSeasonChanged) {
      updateDatesFilter({
        newInitialState: {
          ...initialState.value,
          season: from,
          season_to: to,
        },
        syncWithSeason: !isCurrentSeason,
      });
    } else {
      const state = filtersStorage
        ? { ...filtersStorage }
        : { ...initialState.value, season: from, season_to: to };
      updateDatesFilter({ newInitialState: state });
    }
  }, [
    from,
    to,
    initialState,
    pathname,
    tag,
    handleChangeFilter,
    updateDatesFilter,
  ]);

  return (
    <Form
      fields={fields}
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
    >
      <Box width={1} display={'flex'} justifyContent='flex-end'>
        <Stack direction={'row'} spacing={1} sx={{ mt: 1, mb: 1 }}>
          <ResetButton
            handleResetFilter={onReset}
            initialState={initialState.value}
          />
          <SubmitButton />
        </Stack>
      </Box>
      {children}
    </Form>
  );
};

export default FilterView;
